// src/pages/Dashboard.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Grid, Button, CircularProgress, Avatar, useTheme, IconButton,
  Tooltip, Paper, Stack, Alert as MuiAlert, Skeleton, Link as MuiLink,
  Tabs, Tab, Card, CardContent, CardActions, Chip, Divider, Grow, Fade,
  List, ListItem, ListItemAvatar, ListItemText, ListItemIcon,
  alpha
} from '@mui/material';
import {
  DashboardCustomizeOutlined as DashboardIcon,
  DescriptionOutlined as DescriptionIcon,
  VpnKeyOutlined as ApiKeyIcon,
  ChatBubbleOutlineOutlined as ChatIcon,
  PeopleAltOutlined as PeopleIcon,
  RefreshRounded as RefreshIcon,
  ArrowForwardIosRounded as ArrowIcon,
  BuildOutlined as ToolIcon,
  AddCircleOutlineRounded as AddIcon,
  MemoryOutlined as ModelIcon,
  WidgetsOutlined as WidgetsIcon,
  ChevronRightRounded as ChevronRightRounded,
  AccessTimeFilledRounded as ScheduleIcon,
  BarChartRounded as BarChartRounded,
  SpeedRounded as SpeedIcon,
  ExploreOutlined as ExploreIcon,
  NotificationsNoneRounded as NotificationImportantIcon,
  MenuBookOutlined as MenuBookIcon,
  HelpOutlineRounded as HelpOutlineIcon,
  ShoppingCartOutlined as ShoppingCartIcon,
  WorkspacePremiumRounded as PremiumIcon,
  AccountBalanceWalletOutlined as WalletIcon,
  TrendingUpRounded as TrendingUpRounded,
  SettingsOutlined as SettingsIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

// API Services
import { agentService } from '../api/agentService';
import { widgetService } from '../api/widgetService';
import { apiKeyService } from '../api/apiKeyService';
import { conversationService } from '../api/conversationService';
import { toolService } from '../api/toolService';
import { useModels } from '../hooks/useModels';
import { promptService } from '../api/promptService';

// Context & Billing
import { useBilling } from '../context/BillingContext';
import { billingService } from '../api/billingService';

// --- Re-usable Styled Components ---

const StyledSectionCard = React.forwardRef(({ title, icon, children, headerActions, sx, contentSx, fullHeight = true, ...props }, ref) => {
  const theme = useTheme();
  return (
    <Card
      ref={ref}
      elevation={0}
      sx={{
        display: 'flex', flexDirection: 'column',
        height: fullHeight ? '100%' : 'auto',
        borderRadius: '16px',
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        ...sx
      }}
      {...props}
    >
      {(title || icon || headerActions) && (
        <Box
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            p: theme.spacing(2, 2.5),
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {icon && React.cloneElement(icon, { sx: { color: theme.palette.text.secondary, fontSize: '1.375rem' }})}
            {title && <Typography variant="h6" component="div" fontWeight={600}>{title}</Typography>}
          </Stack>
          {headerActions && <Box>{headerActions}</Box>}
        </Box>
      )}
      <CardContent sx={{ flexGrow: 1, p: 2.5, ...contentSx }}>
        {children}
      </CardContent>
    </Card>
  );
});


const StyledMetricCounter = ({ title, value, icon, color = "primary", isLoading, linkTo, trend, sx }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const metricColors = useMemo(() => ({
    primary: theme.palette.primary,
    secondary: theme.palette.secondary,
    success: theme.palette.success,
    error: theme.palette.error,
    warning: theme.palette.warning,
    info: theme.palette.info,
  }), [theme]);

  const selectedPaletteColor = metricColors[color] || metricColors.primary;
  const mainColor = (typeof selectedPaletteColor?.main === 'string' && selectedPaletteColor.main)
                    ? selectedPaletteColor.main
                    : theme.palette.primary.main;
  const darkColor = (typeof selectedPaletteColor?.dark === 'string' && selectedPaletteColor.dark)
                    ? selectedPaletteColor.dark
                    : mainColor;

  const trendBgColor = useMemo(() => {
    if (!trend) return '';
    const trendPalette = trend.direction === 'up' ? theme.palette.success : theme.palette.error;
    return (typeof trendPalette?.main === 'string' && trendPalette.main)
           ? alpha(trendPalette.main, 0.1)
           : alpha(theme.palette.grey[500], 0.1);
  }, [trend, theme]);

  const trendTextColor = useMemo(() => {
    if (!trend) return '';
    const trendPalette = trend.direction === 'up' ? theme.palette.success : theme.palette.error;
    return (typeof trendPalette?.dark === 'string' && trendPalette.dark)
           ? trendPalette.dark
           : theme.palette.text.primary;
  }, [trend, theme]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        borderRadius: '12px',
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
        transition: theme.transitions.create(['transform', 'box-shadow', 'border-color'], {
          duration: theme.transitions.duration.short,
        }),
        minHeight: 130,
        ...(linkTo && {
          cursor: 'pointer',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: mainColor ? `0 7px 20px ${alpha(mainColor, 0.18)}` : 'none',
            borderColor: mainColor,
        }}),
        ...sx
      }}
      onClick={linkTo ? () => navigate(linkTo) : undefined}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Avatar sx={{
          bgcolor: mainColor ? alpha(mainColor, 0.1) : alpha(theme.palette.primary.main, 0.1),
          color: mainColor,
          width: 44, height: 44,
          borderRadius: '10px',
        }}>
          {icon}
        </Avatar>
        {trend && !isLoading && (
          <Chip
            icon={
              trend.direction === 'up' ?
              <TrendingUpRounded sx={{transform: 'none'}}/> :
              <TrendingUpRounded sx={{transform: trend.direction === 'down' ? 'rotate(90deg)' : 'rotate(0deg)'}}/>
            }
            label={trend.value}
            size="small"
            sx={{bgcolor: trendBgColor, color: trendTextColor, fontWeight:500}}
          />
        )}
      </Stack>
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>{title}</Typography>
        {isLoading ? (<Skeleton variant="text" width="60%" height={36} />) : (
          <Typography variant="h4" fontWeight="bold" sx={{ color: darkColor }}>
            {value}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.round(diffMs / 1000);
    if (diffSec < 2) return 'Just now';
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.round(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHours = Math.round(diffMin / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.round(diffHours / 24);
    return `${diffDays}d ago`;
  } catch (error) {
    console.warn("Error formatting date:", dateString, error);
    return 'Recently';
  }
};
const getConvPreview = (conv) => conv?.first_user_message_preview?.substring(0,45) + (conv?.first_user_message_preview?.length > 45 ? '...' : '') || `Chat ${conv?.id?.slice(-6) || 'ID unknown'}`;

const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [loadingInitialData, setLoadingInitialData] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const { accountStatus, isLoadingStatus: isLoadingBilling, statusError: billingError, refreshAccountStatus } = useBilling();
  const [llmModels, llmModelsLoading, llmModelsError] = useModels([]);

  const [onboardingPlan, setOnboardingPlan] = useState(null);
  const [loadingOnboardingPlan, setLoadingOnboardingPlan] = useState(false);
  const [loadingCheckoutOnboarding, setLoadingCheckoutOnboarding] = useState(false);

  const [dashboardStats, setDashboardStats] = useState({
    llmAvailability: { status: 'neutral', text: 'Checking...' },
    apiKeys: { count: 0, status: 'neutral' },
    metrics: { agents: 0, prompts: 0, tools: 0, widgets: 0, conversations: 0 },
    recentConversations: [],
    alerts: [],
  });
  const [loadingMetrics, setLoadingMetrics] = useState(true);

  const loadingInitialDataRef = React.useRef(loadingInitialData);
  useEffect(() => {
    loadingInitialDataRef.current = loadingInitialData;
  }, [loadingInitialData]);


  const fetchCoreDashboardData = useCallback(async (isRefresh = false) => {
    const wasInitiallyLoading = loadingInitialDataRef.current;

    if (!isRefresh) {
        if (!loadingInitialData) setLoadingInitialData(true);
    } else {
        setIsRefreshing(true);
    }
    setLoadingMetrics(true);

    const parse = (res, type = 'array', property = 'items') => {
      if (res && !res.isError) {
        if (type === 'array') {
          return Array.isArray(res[property]) ? res[property] : (Array.isArray(res) ? res : []);
        }
        return res.total || (Array.isArray(res) ? res.length : 0);
      }
      return type === 'array' ? [] : 0;
    };

    try {
      const [agentsRes, promptsRes, toolsRes, widgetsRes, apiKeysRes, recentConvsRes, totalConvsRes] = await Promise.all([
        agentService.listAgents().catch(e => ({ isError: true, error: e})),
        promptService.getAllPrompts().catch(e => ({ isError: true, error: e})),
        toolService.getAllTools().catch(e => ({ isError: true, error: e})),
        widgetService.listTemplates().catch(e => ({ isError: true, error: e})),
        apiKeyService.getAllKeys().catch(e => ({ isError: true, error: e})),
        conversationService.listConversations(null, 3, 0, null, 'updated_at', 'desc').catch(e => ({ isError: true, error: e})),
        conversationService.listConversations(null, 1, 0, null, null, null, true).catch(e => ({ isError: true, error: e}))
      ]);

      const numApiKeys = parse(apiKeysRes).length;
      const numAgents = parse(agentsRes).length;

      const newAlerts = [];
      if (isRefresh || !wasInitiallyLoading) {
          if (numAgents === 0) newAlerts.push({ id: 'no-agents', severity: 'info', message: 'Ready to build? Create your first AI Agent to get started!', action: <Button size="small" variant="outlined" onClick={() => navigate('/agents/new')}>Create Agent</Button> });
          if (numApiKeys === 0 && numAgents > 0) newAlerts.push({ id: 'no-keys', severity: 'warning', message: 'Integrate your agents by creating an API Key.', action: <Button size="small" variant="outlined" onClick={() => navigate('/apikeys')}>Create API Key</Button> });
          if (billingError) newAlerts.push({ id: 'billing-error', severity: 'error', message: `Billing Error: ${billingError}.`, action: <Button size="small" variant="outlined" onClick={() => navigate('/billing')}>Resolve</Button> });
      }

      setDashboardStats(prev => ({
        ...prev,
        apiKeys: { count: numApiKeys, status: numApiKeys > 0 ? 'success' : 'warning' },
        metrics: {
          agents: numAgents,
          prompts: parse(promptsRes).length,
          tools: parse(toolsRes).length,
          widgets: parse(widgetsRes, 'array', 'templates')?.length || parse(widgetsRes, 'array').length,
          conversations: parse(totalConvsRes, 'total'),
        },
        recentConversations: parse(recentConvsRes, 'array', 'items') || [],
        alerts: newAlerts.length > 0 ? newAlerts : (isRefresh ? [] : prev.alerts)
      }));
      setLastRefreshed(new Date());
    } catch (error) {
      console.error("Dashboard fetch critical error:", error);
      toast.error("Could not load critical dashboard data.");
      setDashboardStats(prev => ({ ...prev, alerts: [{id: 'fetch-error', severity: 'error', message: 'Failed to load dashboard data. Please try refreshing.'}]}));
    } finally {
      setLoadingMetrics(false);
      if (!isRefresh) {
        setLoadingInitialData(false);
      } else {
        setIsRefreshing(false);
      }
    }
  }, [navigate, billingError]);

  useEffect(() => {
    fetchCoreDashboardData();
  }, [fetchCoreDashboardData]);

  useEffect(() => {
    setDashboardStats(prev => ({ ...prev, llmAvailability: {
      status: llmModelsLoading ? 'neutral' : (llmModelsError ? 'error' : (llmModels.length > 0 ? 'success' : 'warning')),
      text: llmModelsLoading ? 'Checking LLM Status...' : (llmModelsError ? 'LLM Service Unavailable' : `${llmModels.length} Model${llmModels.length !== 1 ? 's' : ''} Online`)
    }}));
  }, [llmModels, llmModelsLoading, llmModelsError]);

  const showOnboarding = useMemo(() => !isLoadingBilling && accountStatus && !accountStatus.subscription, [isLoadingBilling, accountStatus]);

  useEffect(() => {
    if (showOnboarding && !onboardingPlan && !loadingOnboardingPlan) {
      setLoadingOnboardingPlan(true);
      billingService.getAvailablePlans().then(res => {
        if (!res.isError && res.subscription_plans) {
          const ftPlan = res.subscription_plans.find(p => p.tier_name === "Free Monthly Trial" || p.is_designated_free_tier);
          setOnboardingPlan(ftPlan || null);
          if (!ftPlan) console.warn("Onboarding: No specific Free Tier plan found. Check plan configuration.");
        } else { console.error("Onboarding: Error loading plans or no plans found.", res.error); }
      }).catch(err => {
        console.error("Onboarding: Failed to fetch plans", err);
        toast.error("Could not load subscription plan details.");
      }).finally(() => setLoadingOnboardingPlan(false));
    }
  }, [showOnboarding, onboardingPlan, loadingOnboardingPlan]);

  const handleActivateOnboardingPlan = async (priceId) => {
    if (!priceId) {
      toast.error("Cannot activate plan: Price ID is missing.");
      return;
    }
    setLoadingCheckoutOnboarding(true);
    toast.info("Redirecting to secure checkout...");
    const successUrl = `${window.location.origin}/billing?checkout=success&action=free_tier_activated`;
    const cancelUrl = `${window.location.origin}/dashboard?checkout=cancel`;
    const result = await billingService.createCheckoutSession({ price_id: priceId, success_url: successUrl, cancel_url: cancelUrl });
    if (result.isError || !result.checkout_url) {
      toast.error(result.error || "Could not initiate checkout. Please try again or contact support.");
      setLoadingCheckoutOnboarding(false);
    } else {
      window.location.href = result.checkout_url;
    }
  };

  const handleRefreshPage = () => { fetchCoreDashboardData(true); refreshAccountStatus(); };

  const quickNavItems = useMemo(() => [
    { title: "Agents", icon: <PeopleIcon />, path: '/agents', colorToken: 'secondary' },
    { title: "Prompts", icon: <DescriptionIcon />, path: '/prompts', colorToken: 'primary' },
    { title: "Tools", icon: <ToolIcon />, path: '/tools', colorToken: 'error' },
    { title: "Widgets", icon: <WidgetsIcon />, path: '/widgets', colorToken: 'info' },
  ], []);


  if (loadingInitialData) {
    return (
      <Box sx={{p: {xs: 2, sm: 3}}}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: '12px' }} />
            <Skeleton variant="text" width={180} height={48} />
          </Stack>
          <Skeleton variant="rounded" width={100} height={36} sx={{ borderRadius: '8px' }} />
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <Stack spacing={3}>
              <Skeleton variant="rounded" height={130} sx={{ borderRadius: '12px' }} />
              <Skeleton variant="rounded" height={130} sx={{ borderRadius: '12px' }} />
              <Skeleton variant="rounded" height={220} sx={{ borderRadius: '16px' }} />
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} lg={5}>
            <Stack spacing={3}>
              <Skeleton variant="rounded" height={180} sx={{ borderRadius: '16px' }} />
              <Skeleton variant="rounded" height={150} sx={{ borderRadius: '16px' }} />
              <Skeleton variant="rounded" height={120} sx={{ borderRadius: '16px' }} />
            </Stack>
          </Grid>
          <Grid item xs={12} md={12} lg={4}>
            <Stack spacing={3}>
              <Skeleton variant="rounded" height={300} sx={{ borderRadius: '16px' }} />
              <Skeleton variant="rounded" height={180} sx={{ borderRadius: '16px' }} />
            </Stack>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Grow in={!loadingInitialData} timeout={300}>
    <Box sx={{p: {xs: 2, sm: 3}, backgroundColor: theme.palette.background.default, minHeight: 'calc(100vh - 64px)' }}>
      <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between" alignItems={{xs:'flex-start', sm:'center'}} mb={4} spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, width: 48, height: 48, borderRadius: '12px' }}>
                <DashboardIcon fontSize="medium"/>
            </Avatar>
            <Typography variant="h3" component="h1" fontWeight="bold">
                Dashboard
            </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
            {lastRefreshed && !isRefreshing && (<Typography variant="caption" color="text.secondary">Updated: {formatTimeAgo(lastRefreshed)}</Typography>)}
            <Button variant="outlined" onClick={handleRefreshPage} disabled={isRefreshing || loadingMetrics} size="medium" startIcon={isRefreshing || loadingMetrics ? <CircularProgress size={18} color="inherit"/> : <RefreshIcon fontSize="small"/>} sx={{textTransform:'none', borderRadius:'8px'}}>
                Refresh
            </Button>
        </Stack>
      </Stack>

      {showOnboarding && (
        <Fade in={showOnboarding} timeout={500}>
        <Paper elevation={0} sx={{ p: {xs: 2, sm: 3}, mb: 3.5, bgcolor: alpha(theme.palette.primary.main, 0.05), border: `1px dashed ${alpha(theme.palette.primary.main, 0.4)}`, borderRadius: '16px'}}>
          <Grid container spacing={{xs:2, md:3}} alignItems="center">
            <Grid item xs={12} md="auto">
                <Avatar sx={{bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText, width: 56, height:56, borderRadius: '12px'}}>
                    <PremiumIcon fontSize="large"/>
                </Avatar>
            </Grid>
            <Grid item xs>
                <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.dark || theme.palette.primary.main }}>
                    Unlock Your AI Potential!
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                    You may have <Chip size="small" label={`${accountStatus?.balance?.toFixed(0) || 'Initial'} Credits`} color="primary" variant="filled" sx={{fontWeight:500}}/>.
                    Activate our <strong>Free Tier</strong> for an extra <Chip size="small" label={`${onboardingPlan?.credits_per_month || 'Bonus'} Credits`} color="success" variant="outlined" sx={{fontWeight:500}}/> monthly, plus 30 days of full feature access!
                </Typography>
            </Grid>
            <Grid item xs={12} md="auto">
              {loadingOnboardingPlan || loadingCheckoutOnboarding ? ( <CircularProgress />
              ) : onboardingPlan ? (
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  color="success"
                  disabled={loadingCheckoutOnboarding}
                  startIcon={loadingCheckoutOnboarding ? <CircularProgress size={20} color="inherit"/> :<PremiumIcon />}
                  onClick={() => handleActivateOnboardingPlan(onboardingPlan.stripe_price_id)}
                  sx={{borderRadius:'10px', py:1.25, textTransform:'none', fontWeight:'bold'}}
                >
                  {loadingCheckoutOnboarding ? 'Processing...' : 'Activate Free Tier'}
                </Button>
              ) : ( <Typography variant="caption" color="text.secondary" sx={{textAlign: 'center', display:'block'}}>Free Tier details loading or unavailable.</Typography> )}
            </Grid>
            {!loadingOnboardingPlan && onboardingPlan && (
              <Grid item xs={12} sx={{textAlign: 'center', pt: 1}}>
                  <MuiLink component={RouterLink} to="/billing" variant="body2" sx={{fontWeight:500}}>
                      Or, Explore All Plans <ArrowIcon fontSize="inherit" sx={{verticalAlign:'middle'}}/>
                  </MuiLink>
              </Grid>
            )}
          </Grid>
        </Paper>
        </Fade>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={3}>
          <Stack spacing={3}>
            <StyledMetricCounter title="Active Agents" value={dashboardStats.metrics.agents} icon={<PeopleIcon />} color="secondary" isLoading={loadingMetrics} linkTo="/agents" />
            <StyledMetricCounter title="Conversations" value={dashboardStats.metrics.conversations} icon={<ChatIcon />} color="warning" isLoading={loadingMetrics} linkTo="/conversations" />

            <StyledSectionCard title="Quick Navigation" icon={<ExploreIcon />} contentSx={{p:1.5}}>
                <List dense disablePadding>
                    {quickNavItems.map(item => {
                        const itemColorMain = (theme.palette[item.colorToken]?.main || theme.palette.primary.main) ;
                        return (
                            <ListItem
                                key={item.title}
                                button
                                component={RouterLink}
                                to={item.path}
                                sx={{borderRadius:'8px', mb:0.5, '&:hover':{bgcolor: alpha(itemColorMain, 0.08)}, transition: 'background-color 0.2s ease'}}
                            >
                                <ListItemAvatar sx={{minWidth: 40}}>
                                    <Avatar sx={{bgcolor: 'transparent', color: itemColorMain, width:30, height:30}}>{item.icon}</Avatar>
                                </ListItemAvatar>
                                <ListItemText primaryTypographyProps={{fontWeight:500}} primary={item.title} />
                                <ChevronRightRounded sx={{color: 'text.disabled'}}/>
                            </ListItem>
                        );
                    })}
                </List>
            </StyledSectionCard>
          </Stack>
        </Grid>

        <Grid item xs={12} md={6} lg={5}>
          <Stack spacing={3}>
            <StyledSectionCard title="Platform Status" icon={<SpeedIcon />}>
                <Stack spacing={1.5}>
                    <ListItem disableGutters>
                        <ListItemIcon sx={{minWidth:36, color: dashboardStats.llmAvailability.status === 'success' ? 'success.main' : (dashboardStats.llmAvailability.status === 'error' ? 'error.main' : 'text.secondary')}}><ModelIcon/></ListItemIcon>
                        <ListItemText primary="LLM Model Service" secondary={dashboardStats.llmAvailability.text} />
                        <Chip label={dashboardStats.llmAvailability.status === 'neutral' ? 'Checking' : dashboardStats.llmAvailability.status} size="small" color={dashboardStats.llmAvailability.status === 'neutral' ? 'default' : dashboardStats.llmAvailability.status} variant="outlined"/>
                    </ListItem>
                     <ListItem disableGutters>
                        <ListItemIcon sx={{minWidth:36, color: dashboardStats.apiKeys.status === 'success' ? 'success.main' : 'warning.main'}}><ApiKeyIcon/></ListItemIcon>
                        <ListItemText primary="API Key Access" secondary={`${dashboardStats.apiKeys.count} key${dashboardStats.apiKeys.count !== 1 ? 's' : ''} active`} />
                        <Chip label={dashboardStats.apiKeys.status === 'success' ? 'Healthy' : (dashboardStats.apiKeys.count > 0 ? 'Active' : 'Needs Setup')} size="small" color={dashboardStats.apiKeys.status === 'success' ? 'success' : (dashboardStats.apiKeys.count > 0 ? 'info' : 'warning')} variant="outlined"/>
                    </ListItem>
                    <ListItem disableGutters>
                        <ListItemIcon sx={{minWidth:36, color: isLoadingBilling ? 'text.secondary' : (billingError ? 'error.main' : 'success.main')}}> <WalletIcon/> </ListItemIcon>
                        <ListItemText primary="Billing Account" secondary={isLoadingBilling ? 'Loading status...' : (billingError ? `Error: ${billingError.substring(0,20)}...` : (accountStatus?.subscription?.status || "No active plan"))} />
                        {!isLoadingBilling &&
                            <Chip label={billingError ? "Error" : (accountStatus?.subscription?.status === 'active' ? "Active" : (accountStatus?.subscription ? accountStatus.subscription.status : "Inactive"))} size="small" color={billingError ? 'error' : (accountStatus?.subscription?.status === 'active' ? "success" : "warning")} variant="outlined"/>
                        }
                    </ListItem>
                </Stack>
            </StyledSectionCard>
            <StyledSectionCard title="System Notices" icon={<NotificationImportantIcon />} fullHeight={false}>
              {loadingMetrics && dashboardStats.alerts.length === 0 ? ( <Box sx={{ display: 'flex', alignItems: 'center', p:1, justifyContent:'center', minHeight: 80 }}><CircularProgress size={24} sx={{mr:1.5}}/><Typography color="text.secondary" variant="body2">Checking for notices...</Typography></Box>
              ) : dashboardStats.alerts.length > 0 ? ( <Stack spacing={1.5}> {dashboardStats.alerts.map((alert) => ( <MuiAlert severity={alert.severity} key={alert.id} variant="standard" action={alert.action} sx={{bgcolor: alpha(theme.palette[alert.severity || 'info']?.main || theme.palette.info.main, 0.1), color: theme.palette[alert.severity || 'info']?.dark || theme.palette.info.dark, borderRadius:'8px', '& .MuiAlert-icon':{color:theme.palette[alert.severity || 'info']?.main || theme.palette.info.main}}}>{alert.message}</MuiAlert> ))} </Stack>
              ) : ( <Typography variant="body2" color="text.secondary" sx={{p:1, textAlign: 'center', fontStyle:'italic', minHeight: 80, display:'flex', alignItems:'center', justifyContent:'center'}}>All systems are operating normally.</Typography>)}
            </StyledSectionCard>
            <StyledSectionCard title="Usage Snapshot" icon={<BarChartRounded />} fullHeight={false}>
                <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', py: 4}}>
                    Detailed usage charts are coming soon.
                    <br/>
                    View your current usage and billing details on the <MuiLink component={RouterLink} to="/billing">Billing Page</MuiLink>.
                </Typography>
            </StyledSectionCard>
          </Stack>
        </Grid>

        <Grid item xs={12} md={12} lg={4}>
          <Stack spacing={3}>
            <StyledSectionCard title="Recent Conversations" icon={<ChatIcon />} headerActions={dashboardStats.recentConversations.length > 0 && (<Button size="small" component={RouterLink} to="/conversations" endIcon={<ArrowIcon fontSize="small"/>} sx={{textTransform:'none'}}>View All</Button>)}>
              {loadingMetrics && dashboardStats.recentConversations.length === 0 ? ( <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}><CircularProgress /><Typography sx={{ml:1.5}} color="text.secondary">Loading activity...</Typography></Box>
              ) : dashboardStats.recentConversations.length === 0 ? ( <Box sx={{ textAlign: 'center', py:4, minHeight: 200, display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center' }}><ChatIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} /> <Typography variant="h6" color="text.secondary">No Conversations Yet</Typography><Typography variant="body2" color="text.disabled">Start interacting with an agent to see activity here.</Typography></Box>
              ) : ( <List dense disablePadding>{dashboardStats.recentConversations.map((conv) => ( <ListItem key={conv.id} button component={RouterLink} to={`/conversations/${conv.id}`} sx={{py:1.5, '&:hover': { bgcolor: theme.palette.action.hover }, borderRadius:'8px'}} > <ListItemAvatar sx={{minWidth: 42}}><Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}><ChatIcon fontSize="small" /></Avatar></ListItemAvatar> <ListItemText primary={<Typography variant="body1" noWrap fontWeight="500" title={getConvPreview(conv)}>{getConvPreview(conv)}</Typography>} secondary={<Stack direction="row" alignItems="center" spacing={0.5}><ScheduleIcon sx={{ fontSize: '0.875rem', color: 'text.disabled' }}/><Typography variant="caption" color="text.secondary">{formatTimeAgo(conv.updated_at)}</Typography></Stack>} /> <ChevronRightRounded sx={{ color: 'text.disabled' }} /> </ListItem> ))} </List> )}
            </StyledSectionCard>
            <StyledSectionCard title="Help & Resources" icon={<HelpOutlineIcon />} contentSx={{p:1.5}}>
                 <List dense disablePadding>
                    <ListItem button component="a" href="https://yourdocs.example.com" target="_blank" rel="noopener noreferrer" sx={{borderRadius:'8px', mb:0.5, '&:hover':{bgcolor: alpha(theme.palette.text.primary, 0.05)}}}>
                        <ListItemAvatar sx={{minWidth: 40}}><Avatar sx={{bgcolor: 'transparent', color: theme.palette.text.secondary, width:30, height:30}}><MenuBookIcon/></Avatar></ListItemAvatar>
                        <ListItemText primaryTypographyProps={{fontWeight:500}} primary="Documentation" />
                        <ChevronRightRounded sx={{color: 'text.disabled'}}/>
                    </ListItem>
                    <ListItem button component={RouterLink} to="/platform-settings" sx={{borderRadius:'8px', mb:0.5, '&:hover':{bgcolor: alpha(theme.palette.text.primary, 0.05)}}}>
                        <ListItemAvatar sx={{minWidth: 40}}><Avatar sx={{bgcolor: 'transparent', color: theme.palette.text.secondary, width:30, height:30}}><SettingsIcon/></Avatar></ListItemAvatar>
                        <ListItemText primaryTypographyProps={{fontWeight:500}} primary="Platform Settings" />
                        <ChevronRightRounded sx={{color: 'text.disabled'}}/>
                    </ListItem>
                 </List>
            </StyledSectionCard>
          </Stack>
        </Grid>
      </Grid>
    </Box>
    </Grow>
  );
};

export default Dashboard;